import _ from 'lodash';

import { ILevelGeneralData } from 'src/app/infrastructure/models/level-general-data';
import { IActivityForm } from './activity-form';
import { IActivityDto, activityDto } from 'src/assets/common/models/activity-dto.g';
import { IActivityRefDto } from 'src/assets/common/models/activity-ref-dto.g';
import { claimData, ClaimData } from '../../claim/models/claim-data';
import { roleData, RoleData } from '../../role/models/role-data';
import { pageListData, PageListData } from '../../page/models/page-list-data';

export interface IActivityCache {
    data: IActivityData;
    url: string;
}

export const activityData = {
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    deleted: 'deleted',
    inactive: 'inactive',

    claimsData: 'claimsData',
    rolesData: 'rolesData',
    pagesData: 'pagesData',
} as const;

export type TActivityData = (typeof activityData)[keyof typeof activityData];

export interface IActivityData extends ILevelGeneralData
{
    [activityData.guid]: string;
    [activityData.nr]: number | null;
    [activityData.name]: string;
    [activityData.deleted]: boolean;
    [activityData.inactive]: boolean;

    [activityData.claimsData]: ClaimData[];
    [activityData.rolesData]: RoleData[];
    [activityData.pagesData]: PageListData[];
}

export class ActivityData
    implements IActivityData, IActivityForm, ILevelGeneralData
{
    [activityData.guid]!: string;
    [activityData.nr]: number | null = null;
    [activityData.name]!: string;;
    [activityData.deleted]!: boolean;
    [activityData.inactive]!: boolean;

    [activityData.claimsData]: ClaimData[] = [];
    [activityData.rolesData]: RoleData[] = [];
    [activityData.pagesData]: PageListData[] = [];

    protected constructor(modelDto?: IActivityDto | IActivityRefDto | null);
    protected constructor(modelData: IActivityData, modelForm: IActivityForm);
    protected constructor(value?: IActivityDto | IActivityRefDto | IActivityData | null, modelForm?: IActivityForm) {
        if (!!modelForm) {
            const _modelData = value as IActivityData;
            _.assign(this, _modelData, modelForm);
        } else if (!!value) {
            const _modelDto = value as IActivityDto;

            this[activityData.guid] = _modelDto[activityDto.guid]!;
            this[activityData.nr] = _modelDto[activityDto.nr];
            this[activityData.name] = _modelDto[activityDto.name]!;
            this[activityData.deleted] = _modelDto[activityDto.deleted]!;
            this[activityData.inactive] = _modelDto[activityDto.inactive]!;

            // claimsDto/rolesDto dragen de platte ref-DTO's (ClaimRefDto/RoleRefDto) om de
            // ActivityDto ⇄ Claim/Role-cyclus te breken; ClaimData/RoleData accepteren de
            // ref-shape en lezen alleen de aanwezige scalairen (ontbrekende velden → null).
            this[activityData.claimsData] = (_modelDto[activityDto.claimsDto]
                ?.map(item => ClaimData.create(item))
                .filter((item): item is ClaimData => item !== null && item[claimData.deleted] === false)
                .sort((a, b) => (a[claimData.name] ?? '').localeCompare(b[claimData.name] ?? '')))
                ?? [];
            this[activityData.rolesData] = (_modelDto[activityDto.rolesDto]
                ?.map(item => RoleData.create(item))
                .filter((item): item is RoleData => item !== null && item[roleData.deleted] === false)
                .sort((a, b) => (a[roleData.name] ?? '').localeCompare(b[roleData.name] ?? '')))
                ?? [];
            // PageActivities: pagina's die deze Activity meekrijgen in hun Details-response.
            this[activityData.pagesData] = (_modelDto[activityDto.pagesDto]
                ?.map(item => PageListData.create(item))
                .filter((item): item is PageListData => item !== null && item[pageListData.deleted] === false)
                .sort((a, b) => (a[pageListData.name] ?? '').localeCompare(b[pageListData.name] ?? '')))
                ?? [];
        }
    }
        
    static create(modelDto?: IActivityDto | IActivityRefDto | null): IActivityData | null
    static create(modelData: IActivityData, modelForm: IActivityForm): IActivityData | null
    static create(value?: IActivityDto | IActivityRefDto | IActivityData | null, modelForm?: IActivityForm): IActivityData | null {

        if (!!modelForm) {
            const _modelData = value as IActivityData;
            return new ActivityData(_modelData, modelForm);
        } else {
            const _modelDto = value as IActivityDto;
            return _modelDto ? new ActivityData(_modelDto) : null;
        }
    }
}
